/**
 * Minimal launcher bootstrap.
 * Loads the full chat bundle only when needed.
 */

import './styles/launcher.css';
import type { BcaiBootConfig, BcaiPublicApi, QueuedCall } from './types';
import { brandSolidColor } from './utils/brandColor';
import { emitAnalytics } from './utils/analytics';
import { dismissProactive, hasOpened, purgeLegacySessions, shouldShowProactive } from './utils/storage';

// Always start each page visit without a restored chat thread.
purgeLegacySessions();

declare global {
  interface Window {
    bcaiBoot?: BcaiBootConfig;
    BudChampionAssistant?: BcaiPublicApi;
  }
}

const queue: QueuedCall[] = [];
let chatReady = false;
let loadingChat = false;
let api: BcaiPublicApi | null = null;
let chatOpen = false;
let unread = 0;

function getBoot(): BcaiBootConfig {
  return window.bcaiBoot ?? {
    version: '0.1.0',
    restUrl: '',
    nonce: '',
    assistantName: 'BudChampion Assistant',
    aiDisclosure: 'AI assistant',
    welcomeMessage: '',
    statusText: 'Replies instantly',
    defaultFlow: 'welcome',
    appearance: {
      launcherSide: 'right',
      launcherOffsetX: 24,
      launcherOffsetY: 24,
      launcherSize: 56,
      accentColor: '#1B5E3B',
      headerBackground: '#0F3D28',
      textColor: '#1A1A1A',
      assistantAvatar: '',
      launcherIcon: '',
      chatWidth: 400,
      chatHeight: 680,
      borderRadius: 18,
      zIndex: 99999,
    },
    proactive: {
      enabled: true,
      text: 'Need help choosing seeds?',
      delay: 8,
      frequency: 'session',
      destination: 'seed-finder',
    },
    suggestedActions: [],
    features: {
      voiceInput: false,
      realtimeVoice: false,
      spokenReplies: false,
      orderLookup: true,
      escalation: true,
      newsletter: false,
      basket: true,
      notificationSound: false,
    },
    persistence: { mode: 'session', days: 7 },
    maxMessageLength: 2000,
    privacy: { conversationNotice: '', voiceNotice: '' },
    analytics: { dataLayer: true, ga4MeasurementId: '' },
    currency: 'GBP',
    cartUrl: '',
    checkoutUrl: '',
    isLoggedIn: false,
    userDisplayName: '',
    userEmail: '',
    chatAssetUrl: '',
    chatCssUrl: '',
    assetsBase: '',
  };
}

function loadStylesheet(href: string): void {
  if (!href || document.querySelector(`link[data-bcai-chat-css="${href}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.bcaiChatCss = href;
  document.head.appendChild(link);
}

const BCAI_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap';

function loadChatFont(): void {
  if (document.querySelector('link[data-bcai-font="plus-jakarta-sans"]')) {
    return;
  }
  if (!document.querySelector('link[data-bcai-font-preconnect="googleapis"]')) {
    const pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';
    pre1.dataset.bcaiFontPreconnect = 'googleapis';
    document.head.appendChild(pre1);
    const pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';
    pre2.dataset.bcaiFontPreconnect = 'gstatic';
    document.head.appendChild(pre2);
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = BCAI_FONT_HREF;
  link.dataset.bcaiFont = 'plus-jakarta-sans';
  document.head.appendChild(link);
}

async function ensureChatLoaded(): Promise<BcaiPublicApi> {
  if (chatReady && api) {
    return api;
  }

  if (loadingChat) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (chatReady && api) {
          window.clearInterval(timer);
          resolve(api);
        } else if (Date.now() - started > 15000) {
          window.clearInterval(timer);
          reject(new Error('Timed out loading BudChampion Assistant.'));
        }
      }, 50);
    });
  }

  loadingChat = true;
  const boot = getBoot();

  try {
    loadChatFont();
    loadStylesheet(boot.chatCssUrl);

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-bcai-chat]');
      if (existing) {
        const loadedSrc = existing.getAttribute('src') || existing.src;
        if (loadedSrc === boot.chatAssetUrl || loadedSrc.endsWith(boot.chatAssetUrl)) {
          resolve();
          return;
        }
        existing.remove();
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = boot.chatAssetUrl;
      script.dataset.bcaiChat = '1';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load chat bundle.'));
      document.head.appendChild(script);
    });

    const mount = (window as Window & { __bcaiMountChat?: (boot: BcaiBootConfig) => BcaiPublicApi })
      .__bcaiMountChat;

    if (!mount) {
      throw new Error('Chat mount function missing.');
    }

    api = mount(boot);
    chatReady = true;
    flushQueue();
    return api;
  } finally {
    loadingChat = false;
  }
}

function flushQueue(): void {
  if (!api) {
    return;
  }

  while (queue.length > 0) {
    const call = queue.shift();
    if (!call) {
      continue;
    }

    const method = api[call.method];
    if (typeof method === 'function') {
      // @ts-expect-error dynamic dispatch of queued API calls
      method(...call.args);
    }
  }
}

function enqueue<K extends keyof BcaiPublicApi>(method: K, args: unknown[]): void {
  queue.push({ method, args });
  void ensureChatLoaded();
}

const publicApi: BcaiPublicApi = {
  open: (...args) => {
    if (api) {
      api.open(...args);
      return;
    }
    enqueue('open', args);
  },
  close: () => {
    if (api) {
      api.close();
      return;
    }
    enqueue('close', []);
  },
  minimise: () => {
    if (api) {
      api.minimise();
      return;
    }
    enqueue('minimise', []);
  },
  newConversation: () => {
    if (api) {
      api.newConversation();
      return;
    }
    enqueue('newConversation', []);
  },
  openFlow: (flow, context) => {
    if (api) {
      api.openFlow(flow, context);
      return;
    }
    enqueue('openFlow', [flow, context]);
  },
  ask: (question) => {
    if (api) {
      api.ask(question);
      return;
    }
    enqueue('ask', [question]);
  },
};

window.BudChampionAssistant = publicApi;

function chatIconSvg(): string {
  return `<svg viewBox="0 0 24 24" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 12c0-4.418 4.03-8 9-8s9 3.582 9 8-4.03 8-9 8c-.62 0-1.22-.05-1.8-.14L5.5 21.5 6.2 17.2C4.8 15.8 4 14 4 12Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
    <circle cx="9.2" cy="12" r="1" fill="currentColor"/>
    <circle cx="13" cy="12" r="1" fill="currentColor"/>
    <circle cx="16.8" cy="12" r="1" fill="currentColor"/>
  </svg>`;
}

function closeIconSvg(): string {
  return `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`;
}

function updateLauncherVisual(button: HTMLButtonElement, boot: BcaiBootConfig): void {
  const icon = button.querySelector('.bcai-launcher__icon');
  const badge = button.querySelector('.bcai-launcher__badge');

  if (icon) {
    icon.innerHTML = chatOpen ? closeIconSvg() : chatIconSvg();
  }

  button.setAttribute('aria-label', chatOpen ? `Close ${boot.assistantName}` : `Open ${boot.assistantName}`);
  button.setAttribute('aria-expanded', chatOpen ? 'true' : 'false');
  button.classList.toggle('is-open', chatOpen);
  button.classList.toggle('has-unread', !chatOpen && unread > 0);

  if (badge) {
    if (!chatOpen && unread > 0) {
      badge.textContent = unread > 9 ? '9+' : String(unread);
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', 'true');
    }
  }
}

function createLauncher(boot: BcaiBootConfig): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'bcai-launcher';
  button.innerHTML = `
    <span class="bcai-launcher__icon" aria-hidden="true">${chatIconSvg()}</span>
    <span class="bcai-launcher__badge" hidden>0</span>
  `;

  const appearance = boot.appearance;
  const side = appearance.launcherSide === 'left' ? 'left' : 'right';
  button.style.setProperty('--bcai-accent', appearance.accentColor);
  button.style.setProperty('--bcai-accent-solid', brandSolidColor(appearance.accentColor));
  button.style.setProperty('--bcai-launcher-size', `${appearance.launcherSize}px`);
  button.style.setProperty('--bcai-z', String(appearance.zIndex));
  button.style.setProperty(side, `max(${appearance.launcherOffsetX}px, env(safe-area-inset-${side}))`);
  button.style.bottom = `max(${appearance.launcherOffsetY}px, env(safe-area-inset-bottom))`;
  if (side === 'left') {
    button.style.right = 'auto';
  } else {
    button.style.left = 'auto';
  }

  button.addEventListener('click', () => {
    if (chatOpen) {
      publicApi.minimise();
    } else {
      unread = 0;
      void publicApi.open();
    }
  });

  updateLauncherVisual(button, boot);
  return button;
}

const BCAI_FLOWS = new Set([
  'open',
  'seed-finder',
  'delivery',
  'payment',
  'track-order',
  'contact-support',
  'escalate',
  'product',
  'ask',
  'promotions',
  'newsletter',
  'notify-promotions',
]);

/**
 * Prefer jQuery-safe hashes: #bcai-seed-finder
 * Legacy #bcai=seed-finder still parses, but themes often crash on the "=".
 */
function normalizeBcaiFlow(raw: string): string {
  const value = raw.trim().toLowerCase();
  if (!value || !BCAI_FLOWS.has(value)) {
    return '';
  }
  return value === 'open' ? 'welcome' : value;
}

function flowFromHashFragment(hash: string): string {
  const h = hash.replace(/^#/, '').trim();
  if (!h) {
    return '';
  }

  // Legacy: bcai=seed-finder&question=...
  if (h.includes('=')) {
    const params = new URLSearchParams(h);
    return normalizeBcaiFlow(params.get('bcai') || '');
  }

  // Safe: bcai-seed-finder or bcai/seed-finder
  const match = h.match(/^bcai[-/]([a-z0-9-]+)$/i);
  return match ? normalizeBcaiFlow(match[1] ?? '') : '';
}

function parseLaunchParams(fromHash?: string): { flow?: string; question?: string; category?: string; productId?: string } {
  const params = new URLSearchParams(window.location.search);
  const hash = (fromHash ?? window.location.hash).replace(/^#/, '');
  const hashParams = new URLSearchParams(hash.includes('=') ? hash : '');

  const flow =
    normalizeBcaiFlow(params.get('bcai') || '')
    || normalizeBcaiFlow(hashParams.get('bcai') || '')
    || flowFromHashFragment(hash);

  if (!flow) {
    return {};
  }

  return {
    flow,
    question: params.get('question') || hashParams.get('question') || undefined,
    category: params.get('category') || hashParams.get('category') || undefined,
    productId: params.get('product_id') || hashParams.get('product_id') || undefined,
  };
}

function applyLaunchParams(launch: { flow?: string; question?: string; category?: string; productId?: string }): boolean {
  if (!launch.flow) {
    return false;
  }
  if (launch.flow === 'ask' && launch.question) {
    publicApi.ask(launch.question);
  } else {
    publicApi.openFlow(launch.flow, {
      category: launch.category,
      productId: launch.productId,
    });
  }
  return true;
}

function parseBcaiHref(href: string): { flow?: string; question?: string; category?: string; productId?: string } {
  const trimmed = href.trim();
  if (!trimmed || !/bcai[=/-]/i.test(trimmed)) {
    return {};
  }

  try {
    const url = new URL(trimmed, window.location.href);
    const hash = url.hash.replace(/^#/, '');
    const hashParams = new URLSearchParams(hash.includes('=') ? hash : '');
    const flow =
      normalizeBcaiFlow(url.searchParams.get('bcai') || '')
      || normalizeBcaiFlow(hashParams.get('bcai') || '')
      || flowFromHashFragment(hash);

    if (!flow) {
      return {};
    }

    return {
      flow,
      question: url.searchParams.get('question') || hashParams.get('question') || undefined,
      category: url.searchParams.get('category') || hashParams.get('category') || undefined,
      productId: url.searchParams.get('product_id') || hashParams.get('product_id') || undefined,
    };
  } catch {
    return parseLaunchParams(trimmed.startsWith('#') ? trimmed : undefined);
  }
}

function flowFromClassList(el: Element | null): string {
  if (!el) {
    return '';
  }
  for (const cls of Array.from(el.classList)) {
    const match = cls.match(/^bcai-open-([a-z0-9-]+)$/i);
    if (!match?.[1]) {
      continue;
    }
    const raw = match[1].toLowerCase();
    return normalizeBcaiFlow(raw === 'assistant' ? 'open' : raw);
  }
  return '';
}

function resolveFlowFromMenuTarget(target: HTMLElement): string {
  const attr = target.getAttribute('data-bcai-open');
  if (attr) {
    const normalized = normalizeBcaiFlow(attr);
    return normalized || (attr === 'welcome' ? 'welcome' : '');
  }
  return (
    flowFromClassList(target)
    || flowFromClassList(target.closest('.menu-item, .w-nav-item, li'))
    || ''
  );
}

function bindDataAttributes(): void {
  const openFromEventTarget = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest?.(
      'a, button, [data-bcai-open], [class*="bcai-open-"]',
    ) as HTMLElement | null;
    if (!target) {
      return;
    }

    let flow = resolveFlowFromMenuTarget(target);
    if (!flow && target instanceof HTMLAnchorElement) {
      flow = parseBcaiHref(target.getAttribute('href') || '').flow || '';
    }
    if (!flow) {
      const anchor = target.closest('a');
      if (anchor) {
        flow = resolveFlowFromMenuTarget(anchor) || parseBcaiHref(anchor.getAttribute('href') || '').flow || '';
      }
    }
    if (!flow) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const category = target.getAttribute('data-bcai-category')
      || target.closest('[data-bcai-category]')?.getAttribute('data-bcai-category')
      || undefined;
    const productId = target.getAttribute('data-bcai-product-id')
      || target.closest('[data-bcai-product-id]')?.getAttribute('data-bcai-product-id')
      || undefined;

    publicApi.openFlow(flow, {
      category: category || undefined,
      productId: productId || undefined,
    });
  };

  document.addEventListener('click', openFromEventTarget, true);

  const stampMenuLinks = (root: ParentNode = document): void => {
    const anchors = new Set<HTMLAnchorElement>();

    root.querySelectorAll('a[href*="bcai"]').forEach((node) => {
      if (node instanceof HTMLAnchorElement) {
        anchors.add(node);
      }
    });

    root.querySelectorAll('[class*="bcai-open-"]').forEach((node) => {
      if (node instanceof HTMLAnchorElement) {
        anchors.add(node);
        return;
      }
      const inner = node.querySelector('a');
      if (inner instanceof HTMLAnchorElement) {
        anchors.add(inner);
      }
    });

    anchors.forEach((node) => {
      const href = node.getAttribute('href') || '';
      const flowAttr = resolveFlowFromMenuTarget(node) || parseBcaiHref(href).flow || '';
      if (!flowAttr) {
        return;
      }

      const dataFlow = flowAttr === 'welcome' ? 'open' : flowAttr;
      node.setAttribute('data-bcai-open', dataFlow);

      // Avoid query-string reloads and legacy equals hashes.
      if (/[?&]bcai=/i.test(href) || href.includes('?bcai=')) {
        node.setAttribute('href', '#');
      } else if (/#bcai=/i.test(href)) {
        node.setAttribute('href', href.replace(/#bcai=/i, '#bcai-'));
      } else if (!href || href === '#') {
        node.setAttribute('href', '#');
      }

      if (node.dataset.bcaiBound !== '1') {
        node.dataset.bcaiBound = '1';
        const flow = dataFlow;
        node.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          publicApi.openFlow(flow);
          return false;
        };
      }
    });
  };

  stampMenuLinks();
  window.setTimeout(() => stampMenuLinks(), 0);
  window.setTimeout(() => stampMenuLinks(), 500);

  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            stampMenuLinks(node);
          }
        });
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.addEventListener('hashchange', () => {
    applyLaunchParams(parseLaunchParams());
  });
}

function maybeShowProactive(boot: BcaiBootConfig, host: HTMLElement): void {
  if (!boot.proactive.enabled || !shouldShowProactive(boot.proactive.frequency)) {
    return;
  }

  window.setTimeout(() => {
    if (hasOpened() || chatOpen) {
      return;
    }

    const side = boot.appearance.launcherSide === 'left' ? 'left' : 'right';
    const prompt = document.createElement('div');
    prompt.className = `bcai-proactive bcai-proactive--${side}`;
    prompt.setAttribute('role', 'dialog');
    prompt.setAttribute('aria-label', 'Assistant help prompt');
    prompt.style.setProperty('--bcai-z', String(boot.appearance.zIndex));
    prompt.style.setProperty(side, `max(${boot.appearance.launcherOffsetX}px, env(safe-area-inset-${side}))`);
    prompt.style.bottom = `calc(max(${boot.appearance.launcherOffsetY}px, env(safe-area-inset-bottom)) + ${boot.appearance.launcherSize + 16}px)`;
    prompt.innerHTML = `
      <button type="button" class="bcai-proactive__close" aria-label="Dismiss help prompt">&times;</button>
      <button type="button" class="bcai-proactive__body">${boot.proactive.text}</button>
    `;

    prompt.querySelector('.bcai-proactive__close')?.addEventListener('click', () => {
      dismissProactive(boot.proactive.frequency);
      prompt.remove();
    });

    prompt.querySelector('.bcai-proactive__body')?.addEventListener('click', () => {
      dismissProactive(boot.proactive.frequency);
      emitAnalytics({ event: 'proactive_prompt_clicked' }, boot);
      publicApi.openFlow(boot.proactive.destination || 'welcome');
      prompt.remove();
    });

    host.appendChild(prompt);
    emitAnalytics({ event: 'proactive_prompt_shown' }, boot);
  }, Math.max(0, boot.proactive.delay) * 1000);
}

function init(): void {
  try {
    const boot = getBoot();
    loadChatFont();
    const host = document.createElement('div');
    host.id = 'bcai-root-host';
    host.className = 'bcai-root-host';
    document.body.appendChild(host);

    const launcher = createLauncher(boot);
    host.appendChild(launcher);
    emitAnalytics({ event: 'launcher_impression' }, boot);
    bindDataAttributes();

    document.addEventListener('bcai:chat-open-state', ((event: CustomEvent<{ open: boolean }>) => {
      chatOpen = Boolean(event.detail?.open);
      if (chatOpen) {
        unread = 0;
        host.querySelector('.bcai-proactive')?.remove();
      }
      updateLauncherVisual(launcher, boot);
    }) as EventListener);

    // Pulse after inactivity if still closed.
    window.setTimeout(() => {
      if (!chatOpen && !hasOpened()) {
        launcher.classList.add('is-pulse');
      }
    }, 20000);

    const launch = parseLaunchParams();
    if (applyLaunchParams(launch)) {
      // Deep-linked open — skip proactive bubble.
    } else {
      maybeShowProactive(boot, host);
    }

    window.setTimeout(() => {
      const ric = (window as Window & {
        requestIdleCallback?: (callback: () => void) => number;
      }).requestIdleCallback;

      if (typeof ric === 'function') {
        ric(() => {
          void ensureChatLoaded().catch(() => undefined);
        });
      }
    }, 12000);
  } catch (error) {
    console.error('[BCAI] launcher init failed', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
